import { useState, useEffect } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Warning, Plus, MagnifyingGlass, CurrencyDollar, Pencil
} from '@phosphor-icons/react';
import SortableTable from '../components/SortableTable';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PENALTY_TYPES = ['late_shipment', 'cancellation', 'sla_breach', 'quality_issue', 'manual'];

export default function AdminPenaltiesPage() {
  const [penalties, setPenalties] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ seller_id: '', type: 'manual', reason: '', amount: '', order_id: '' });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [penRes, selRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/penalties`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/sellers`, { withCredentials: true }),
      ]);
      setPenalties(penRes.data);
      setSellers(selRes.data.sellers || selRes.data || []);
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  };

  const handleCreate = async () => {
    const seller = sellers.find(s => s.user_id === form.seller_id);
    try {
      await axios.post(`${API_URL}/api/admin/penalties`, {
        ...form,
        seller_name: seller?.name || '',
        amount: parseFloat(form.amount) || 0,
      }, { withCredentials: true });
      toast.success('Penalty created');
      setDialogOpen(false);
      setForm({ seller_id: '', type: 'manual', reason: '', amount: '', order_id: '' });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleStatusChange = async (penaltyId, status) => {
    try {
      await axios.patch(`${API_URL}/api/admin/penalties/${penaltyId}`, { status }, { withCredentials: true });
      toast.success(`Penalty ${status}`);
      fetchData();
    } catch { toast.error('Failed'); }
  };

  const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n || 0);

  const filtered = penalties.filter(p =>
    !search || p.seller_name?.toLowerCase().includes(search.toLowerCase()) || p.reason?.toLowerCase().includes(search.toLowerCase())
  );

  const totalActive = penalties.filter(p => p.status === 'active').reduce((s, p) => s + (p.amount || 0), 0);
  const totalWaived = penalties.filter(p => p.status === 'waived').length;

  const columns = [
    { key: 'seller_name', label: 'Seller' },
    { key: 'type', label: 'Type', render: v => <span className="text-xs font-semibold uppercase tracking-wider px-2 py-1 bg-[#fcfcfc] rounded">{v?.replace('_', ' ')}</span> },
    { key: 'reason', label: 'Reason', render: v => <span className="truncate max-w-[200px] inline-block text-sm">{v}</span> },
    { key: 'amount', label: 'Amount', render: v => <span className="font-bold text-[#ea4b5a]">{fmt(v)}</span> },
    { key: 'order_id', label: 'Order', render: v => v ? <span className="font-mono text-xs">{v.slice(-10)}</span> : '-' },
    { key: 'status', label: 'Status', render: v => {
      const c = v === 'active' ? '#ea4b5a' : v === 'waived' ? '#9c9db7' : v === 'deducted' ? '#28cd8c' : '#f6a609';
      return <span className="px-2 py-0.5 text-[11px] font-bold rounded capitalize" style={{ backgroundColor: `${c}15`, color: c }}>{v}</span>;
    }},
    { key: 'created_at', label: 'Date', render: v => new Date(v).toLocaleDateString() },
    { key: 'penalty_id', label: 'Actions', sortable: false, render: (v, row) => (
      <div className="flex gap-1">
        {row.status === 'active' && (
          <>
            <Button size="sm" variant="outline" className="text-xs" onClick={() => handleStatusChange(v, 'waived')} data-testid={`waive-penalty-${v}`}>Waive</Button>
            <Button size="sm" className="bg-[#28cd8c] hover:bg-[#1fb076] text-white text-xs" onClick={() => handleStatusChange(v, 'deducted')} data-testid={`deduct-penalty-${v}`}>Deduct</Button>
          </>
        )}
      </div>
    )}
  ];

  return (
    <DashboardLayout title="Seller Penalties" subtitle="Manage penalties and deductions">
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-white border border-[#e5e5ea] rounded-xl p-4" data-testid="stat-total-penalties">
          <p className="text-xs text-[#9c9db7] uppercase tracking-wider font-semibold">Total Penalties</p>
          <p className="text-2xl font-bold text-[#0A0A0A] mt-1">{penalties.length}</p>
        </div>
        <div className="bg-white border border-[#e5e5ea] rounded-xl p-4" data-testid="stat-active-amount">
          <p className="text-xs text-[#9c9db7] uppercase tracking-wider font-semibold">Active Amount</p>
          <p className="text-2xl font-bold text-[#ea4b5a] mt-1">{fmt(totalActive)}</p>
        </div>
        <div className="bg-white border border-[#e5e5ea] rounded-xl p-4" data-testid="stat-waived">
          <p className="text-xs text-[#9c9db7] uppercase tracking-wider font-semibold">Waived</p>
          <p className="text-2xl font-bold text-[#9c9db7] mt-1">{totalWaived}</p>
        </div>
      </div>

      <div className="bg-white border border-[#e5e5ea] mb-6">
        <div className="action-bar">
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5a5c87]" size={18} />
            <Input placeholder="Search by seller or reason..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10 rounded-sm" data-testid="penalty-search" />
          </div>
          <Button onClick={() => { setForm({ seller_id: '', type: 'manual', reason: '', amount: '', order_id: '' }); setDialogOpen(true); }} className="bg-[#ea4b5a] hover:bg-[#c43447] text-white" data-testid="add-penalty-btn">
            <Plus size={16} className="mr-1" /> Add Penalty
          </Button>
        </div>
      </div>

      {loading ? <div className="loading-spinner"><div className="spinner"></div></div> : (
        <SortableTable columns={columns} data={filtered} pageSize={15} emptyMessage="No penalties" emptyIcon={Warning} />
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Penalty</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">Seller *</label>
              <Select value={form.seller_id || "none"} onValueChange={v => setForm({ ...form, seller_id: v === "none" ? "" : v })}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="penalty-seller-select"><SelectValue placeholder="Select seller" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Select seller</SelectItem>
                  {sellers.map(s => <SelectItem key={s.user_id} value={s.user_id}>{s.name} ({s.email})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">Type</label>
                <Select value={form.type} onValueChange={v => setForm({ ...form, type: v })}>
                  <SelectTrigger className="mt-1 rounded-sm" data-testid="penalty-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PENALTY_TYPES.map(t => <SelectItem key={t} value={t}>{t.replace('_', ' ')}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">Amount (Rs.)</label>
                <Input type="number" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} className="mt-1 rounded-sm" data-testid="penalty-amount-input" />
              </div>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">Reason *</label>
              <Textarea value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })} className="mt-1 rounded-sm" rows={2} data-testid="penalty-reason-input" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">Related Order ID (optional)</label>
              <Input value={form.order_id} onChange={e => setForm({ ...form, order_id: e.target.value })} className="mt-1 rounded-sm" data-testid="penalty-order-input" />
            </div>
            <div className="flex gap-4 pt-2">
              <Button variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">Cancel</Button>
              <Button onClick={handleCreate} disabled={!form.seller_id || !form.reason} className="flex-1 bg-[#ea4b5a] hover:bg-[#c43447] text-white" data-testid="save-penalty-btn">Create Penalty</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
